import Layout from "@/components/Layout";
import { motion } from "framer-motion";
import { useState } from "react";
import { Link } from "wouter";
import { useSEO } from "@/hooks/useSEO";
import { Check, Zap, ArrowRight, ChevronDown, Clock, BadgePercent, FileText, MessageCircle, Calculator } from "lucide-react";
import {
  KIRALAMA_PAKETLERI,
  DANISMANLIK_PAKETLERI,
  FIYAT_KOSULLARI,
  FIYAT_SSS,
  ORNEK_HESAP,
  danismanlikFiyati,
  waLink,
} from "@/lib/fiyat_data";

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.5 },
};

const PLATFORM_RENK: Record<string, string> = { Meta: "#1877F2", Google: "#4285F4", TikTok: "#000000" };
const tl = (n: number) => `${n.toLocaleString("tr-TR")} TL`;

function BolumBasligi({ etiket, baslik, vurgu, metin }: { etiket: string; baslik: string; vurgu: string; metin: string }) {
  return (
    <motion.div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12" {...fadeInUp}>
      <span className="inline-block border-3 border-[#0A1A3F] bg-[#6B8AFE]/10 px-3 py-1.5 mb-4 text-xs font-bold text-[#0A1A3F] uppercase tracking-wider shadow-[2px_2px_0_0_#0A1A3F]">
        {etiket}
      </span>
      <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black text-[#0A1A3F] leading-tight">
        {baslik} <span className="text-[#6B8AFE]">{vurgu}</span>
      </h2>
      <p className="mt-4 text-sm sm:text-lg text-[#0A1A3F]/70 leading-relaxed">{metin}</p>
    </motion.div>
  );
}

function OzellikListesi({ ozellikler }: { ozellikler: string[] }) {
  return (
    <ul className="space-y-2.5">
      {ozellikler.map((o) => (
        <li key={o} className="flex items-start gap-2.5 text-sm text-[#0A1A3F] font-medium leading-snug">
          <Check size={16} className="text-[#6B8AFE] flex-shrink-0 mt-0.5" strokeWidth={3} />
          <span>{o}</span>
        </li>
      ))}
    </ul>
  );
}

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-3 border-[#0A1A3F] bg-white shadow-[3px_3px_0_0_#0A1A3F]">
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 p-4 sm:p-5 text-left"
      >
        <span className="font-bold text-[#0A1A3F] text-sm sm:text-base">{q}</span>
        <ChevronDown size={20} className={`text-[#6B8AFE] flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="px-4 sm:px-5 pb-5 text-sm text-[#0A1A3F]/75 leading-relaxed">{a}</p>}
    </div>
  );
}

export default function Pricing() {
  useSEO();
  const ornekKomisyon = (ORNEK_HESAP.harcama * ORNEK_HESAP.oran) / 100;

  return (
    <Layout>
      {/* ═══════════════════════ HERO ═══════════════════════ */}
      <section className="bg-[#FFFDF7] py-12 sm:py-20 border-b-3 border-[#0A1A3F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center max-w-3xl mx-auto" {...fadeInUp}>
            <div className="inline-flex items-center gap-2 border-3 border-[#0A1A3F] bg-[#6B8AFE]/10 px-4 py-2 mb-6 shadow-[2px_2px_0_0_#0A1A3F]">
              <Zap size={16} className="text-[#6B8AFE]" />
              <span className="text-xs sm:text-sm font-bold text-[#0A1A3F] uppercase tracking-wider">Fiyatlandırma</span>
            </div>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-[#0A1A3F] leading-tight">
              Hesap Kiralama ve <span className="text-[#6B8AFE]">Reklam Danışmanlığı</span> Fiyatları
            </h1>
            <p className="mt-5 sm:mt-6 text-base sm:text-lg text-[#0A1A3F]/70 leading-relaxed">
              Ajans reklam hesabında yalnızca reklam bakiyesi üzerinden %3'ten başlayan komisyon ödersiniz. Reklamlarınızı
              uzman ekibe bırakmak isterseniz aylık yönetim paketleriyle çalışırız.
            </p>
            <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3 text-left sm:text-center">
              {[
                { ikon: BadgePercent, t: "Kurulum ücreti yok" },
                { ikon: FileText, t: "Minimum bütçe yok" },
                { ikon: Clock, t: "Aynı gün aktivasyon" },
              ].map(({ ikon: Ikon, t }) => (
                <div key={t} className="flex sm:justify-center items-center gap-2 border-2 border-[#0A1A3F] bg-white px-3 py-2.5 text-sm font-bold text-[#0A1A3F]">
                  <Ikon size={16} className="text-[#6B8AFE] flex-shrink-0" />
                  {t}
                </div>
              ))}
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <a href="#hesap-kiralama" className="brutalist-btn bg-[#6B8AFE] text-[#0A1A3F] inline-flex items-center justify-center gap-2 text-sm sm:text-base">
                Hesap Kiralama Fiyatları <ArrowRight size={18} />
              </a>
              <a href="#reklam-danismanligi" className="brutalist-btn bg-white text-[#0A1A3F] inline-flex items-center justify-center gap-2 text-sm sm:text-base">
                Danışmanlık Paketleri <ArrowRight size={18} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════ HESAP KİRALAMA ═══════════════════════ */}
      <section id="hesap-kiralama" className="scroll-mt-20 sm:scroll-mt-24 bg-white py-12 sm:py-20 border-b-3 border-[#0A1A3F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <BolumBasligi
            etiket="Ajans reklam hesabı"
            baslik="Reklam Hesabı"
            vurgu="Kiralama Fiyatları"
            metin="Meta, Google Ads ve TikTok için yüksek limitli ajans hesapları. Sabit aylık ücret yok; komisyon reklam bakiyesi üzerinden alınır ve hacminiz büyüdükçe düşer."
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-5 lg:gap-8">
            {KIRALAMA_PAKETLERI.map((k) => (
              <motion.div
                key={k.id}
                {...fadeInUp}
                className={`relative flex flex-col border-3 border-[#0A1A3F] bg-white min-w-0 ${
                  k.oneCikan ? "shadow-[6px_6px_0_0_#6B8AFE]" : "shadow-[4px_4px_0_0_#0A1A3F]"
                }`}
              >
                {k.oneCikan && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap border-2 border-[#0A1A3F] bg-[#6B8AFE] px-3 py-0.5 text-[11px] font-black uppercase tracking-wider text-[#0A1A3F]">
                    En çok tercih edilen
                  </span>
                )}
                <div className="p-5 sm:p-6 border-b-3 border-[#0A1A3F]">
                  <div className="flex items-center gap-3 mb-3">
                    <span
                      className="w-9 h-9 flex items-center justify-center text-white font-black text-sm border-2 border-[#0A1A3F] flex-shrink-0"
                      style={{ backgroundColor: PLATFORM_RENK[k.platform] }}
                    >
                      {k.platform.charAt(0)}
                    </span>
                    <h3 className="text-lg font-black text-[#0A1A3F] leading-tight">{k.baslik}</h3>
                  </div>
                  <p className="text-sm text-[#0A1A3F]/70 leading-relaxed">{k.aciklama}</p>
                  <div className="mt-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-[#0A1A3F]/60">Komisyon</p>
                    <p className="text-3xl sm:text-4xl font-black text-[#0A1A3F] leading-tight">{k.komisyon}</p>
                    <p className="text-xs text-[#0A1A3F]/60 font-medium">{k.komisyonNot}</p>
                  </div>
                </div>
                <div className="p-5 sm:p-6 flex flex-col flex-1">
                  <OzellikListesi ozellikler={k.ozellikler} />
                  <div className="pt-6 flex flex-col gap-2 mt-auto">
                    <a
                      href={waLink(k.whatsapp)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`brutalist-btn text-center inline-flex items-center justify-center gap-2 text-sm ${k.oneCikan ? "bg-[#6B8AFE] text-[#0A1A3F]" : "bg-white text-[#0A1A3F]"}`}
                    >
                      <MessageCircle size={16} /> Teklif Alın
                    </a>
                    <Link href={k.detay} className="text-center text-xs font-bold uppercase tracking-wider text-[#0A1A3F] underline underline-offset-4 py-2">
                      Hizmet detayları
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Örnek hesap */}
          <motion.div {...fadeInUp} className="mt-10 sm:mt-14 border-3 border-[#0A1A3F] bg-[#0A1A3F] text-white p-5 sm:p-8 shadow-[6px_6px_0_0_#6B8AFE]">
            <div className="flex items-center gap-2 mb-4">
              <Calculator size={20} className="text-[#6B8AFE]" />
              <h3 className="font-black text-lg sm:text-xl">Örnek komisyon hesabı</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-3 sm:gap-4 text-center">
              <div className="border-2 border-white/30 p-3 sm:p-4">
                <p className="text-[11px] uppercase tracking-wider text-white/60 font-bold">Aylık reklam harcaması</p>
                <p className="text-xl sm:text-2xl font-black">{tl(ORNEK_HESAP.harcama)}</p>
              </div>
              <span className="text-2xl font-black text-[#6B8AFE]">×</span>
              <div className="border-2 border-white/30 p-3 sm:p-4">
                <p className="text-[11px] uppercase tracking-wider text-white/60 font-bold">Başlangıç komisyonu</p>
                <p className="text-xl sm:text-2xl font-black">%{ORNEK_HESAP.oran}</p>
              </div>
              <span className="text-2xl font-black text-[#6B8AFE]">=</span>
              <div className="border-2 border-[#6B8AFE] bg-[#6B8AFE]/15 p-3 sm:p-4">
                <p className="text-[11px] uppercase tracking-wider text-white/60 font-bold">Hizmet komisyonu</p>
                <p className="text-xl sm:text-2xl font-black text-[#6B8AFE]">{tl(ornekKomisyon)}</p>
              </div>
            </div>
            <p className="mt-4 text-xs sm:text-sm text-white/60 leading-relaxed">
              Hesaplama başlangıç oranıyla yapılmış bir örnektir. Net oran aylık harcama hacminize göre belirlenir ve hacim büyüdükçe düşer.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════ DANIŞMANLIK ═══════════════════════ */}
      <section id="reklam-danismanligi" className="scroll-mt-20 sm:scroll-mt-24 bg-[#FFFDF7] py-12 sm:py-20 border-b-3 border-[#0A1A3F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <BolumBasligi
            etiket="Uzman ekip"
            baslik="Reklam Danışmanlığı ve"
            vurgu="Yönetim Paketleri"
            metin="Ücretsiz ön denetimle başlarız. Kampanyalarınızı bize bırakmak isterseniz aylık yönetim paketleriyle Meta, Google ve TikTok reklamlarınızı birlikte büyütürüz."
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-5 lg:gap-8">
            {DANISMANLIK_PAKETLERI.map((d) => (
              <motion.div
                key={d.id}
                {...fadeInUp}
                className={`relative flex flex-col border-3 border-[#0A1A3F] min-w-0 ${
                  d.oneCikan ? "bg-[#6B8AFE]/5 shadow-[6px_6px_0_0_#6B8AFE]" : "bg-white shadow-[4px_4px_0_0_#0A1A3F]"
                }`}
              >
                {d.oneCikan && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap border-2 border-[#0A1A3F] bg-[#6B8AFE] px-3 py-0.5 text-[11px] font-black uppercase tracking-wider text-[#0A1A3F]">
                    Önerilen
                  </span>
                )}
                <div className="p-5 sm:p-6 border-b-3 border-[#0A1A3F]">
                  <h3 className="text-lg font-black text-[#0A1A3F] leading-tight">{d.baslik}</h3>
                  <p className="mt-2 text-sm text-[#0A1A3F]/70 leading-relaxed">{d.aciklama}</p>
                  <div className="mt-4">
                    <p className="text-3xl sm:text-4xl font-black text-[#0A1A3F] leading-tight">
                      {danismanlikFiyati(d)}
                      {d.fiyat != null && d.fiyat > 0 && <span className="text-base font-bold text-[#0A1A3F]/60"> {d.donem} + KDV</span>}
                    </p>
                    <p className="text-xs text-[#0A1A3F]/60 font-medium">{d.fiyatNot}</p>
                  </div>
                </div>
                <div className="p-5 sm:p-6 flex flex-col flex-1">
                  <OzellikListesi ozellikler={d.ozellikler} />
                  <div className="pt-6 flex flex-col gap-2 mt-auto">
                    <a
                      href={waLink(d.whatsapp)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`brutalist-btn text-center inline-flex items-center justify-center gap-2 text-sm ${d.oneCikan ? "bg-[#6B8AFE] text-[#0A1A3F]" : "bg-white text-[#0A1A3F]"}`}
                    >
                      <MessageCircle size={16} /> {d.fiyat === 0 ? "Görüşme Planlayın" : "Teklif Alın"}
                    </a>
                    {d.detay && (
                      <Link href={d.detay} className="text-center text-xs font-bold uppercase tracking-wider text-[#0A1A3F] underline underline-offset-4 py-2">
                        Hizmet detayları
                      </Link>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <p className="mt-8 text-center text-sm text-[#0A1A3F]/70 font-medium max-w-2xl mx-auto">
            Reklam yönetimi için aylık 30.000 TL ve üzeri reklam bütçesi öneriyoruz. Ajans hesabı ile yönetimi birlikte alan markalara avantajlı komisyon uygulanır.
          </p>
        </div>
      </section>

      {/* ═══════════════════════ KOŞULLAR ═══════════════════════ */}
      <section className="bg-white py-12 sm:py-20 border-b-3 border-[#0A1A3F]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <BolumBasligi etiket="Şeffaf model" baslik="Tüm Hizmetlerde" vurgu="Geçerli Koşullar" metin="Teklifte göreceğiniz rakam dışında ödeme yoktur." />
          <dl className="border-3 border-[#0A1A3F] shadow-[4px_4px_0_0_#0A1A3F] divide-y-2 divide-[#0A1A3F]/15 bg-white">
            {FIYAT_KOSULLARI.map((k) => (
              <div key={k.baslik} className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-4 px-4 sm:px-6 py-3.5">
                <dt className="text-sm font-bold text-[#0A1A3F]/70">{k.baslik}</dt>
                <dd className="text-sm sm:text-base font-black text-[#0A1A3F] flex items-start gap-2">
                  <Check size={16} className="text-[#6B8AFE] flex-shrink-0 mt-1" strokeWidth={3} />
                  {k.deger}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* ═══════════════════════ SSS ═══════════════════════ */}
      <section className="bg-[#FFFDF7] py-12 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 className="text-2xl sm:text-4xl font-black text-[#0A1A3F] text-center mb-8 sm:mb-10" {...fadeInUp}>
            Fiyatlarla İlgili <span className="text-[#6B8AFE]">Sorular</span>
          </motion.h2>
          <div className="space-y-4">
            {FIYAT_SSS.map((f) => (
              <FAQItem key={f.q} q={f.q} a={f.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════ CTA ═══════════════════════ */}
      <section className="bg-[#0A1A3F] py-12 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-4xl font-black text-white leading-tight">
            Net teklifinizi <span className="text-[#6B8AFE]">bugün</span> alın
          </h2>
          <p className="mt-4 text-white/70 text-sm sm:text-base leading-relaxed">
            Aylık harcama hedefinizi ve hangi platformlarda reklam verdiğinizi yazın; komisyon oranınızı ve yönetim teklifinizi aynı gün iletelim.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={waLink("Merhaba, AdsManager hesap kiralama ve reklam yönetimi fiyatları hakkında bilgi almak istiyorum.")}
              target="_blank"
              rel="noopener noreferrer"
              className="brutalist-btn bg-[#6B8AFE] text-[#0A1A3F] inline-flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              <MessageCircle size={18} /> WhatsApp'tan Teklif Alın
            </a>
            <a href="tel:+905320522545" className="brutalist-btn bg-white text-[#0A1A3F] inline-flex items-center justify-center gap-2 text-sm sm:text-base">
              0532 052 25 45
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
